<div class="card">
    <div class="font-semibold text-xl mb-4">Consultez votre historique de prédictions et suivez vos résultats !</div>
    <p>Retrouvez les probabilités que vous avez reçues, les matchs que vous avez suivis, et leur issue finale.</p>
</div>

<div class="card">
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow p-6">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Votre ROI moyen</p>
                    <p class="text-2xl font-bold text-blue-600">
                        <ng-container *ngIf="!isLoadingRoi(); else loadingSkeleton">
                            {{ getAverageUserRoi() }}%
                        </ng-container>
                    </p>
                </div>
                <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <i class="pi pi-user text-blue-600"></i>
                </div>
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">ROI Coteo moyen</p>
                    <p class="text-2xl font-bold text-orange-600">
                        <ng-container *ngIf="!isLoadingRoi(); else loadingSkeleton">
                            {{ getAveragePlatformRoi() }}%
                        </ng-container>
                    </p>
                </div>
                <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <i class="pi pi-chart-line text-orange-600"></i>
                </div>
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Différence</p>
                    <p class="text-2xl font-bold" [ngClass]="getPerformanceColor()">
                        <ng-container *ngIf="!isLoadingRoi(); else loadingSkeleton">
                            {{ getPerformanceDifference() }}%
                        </ng-container>
                    </p>
                </div>
                <div class="w-12 h-12 rounded-full flex items-center justify-center" [ngClass]="{
                        'bg-green-100': getPerformanceStatus() === 'excellent' || getPerformanceStatus() === 'good',
                        'bg-yellow-100': getPerformanceStatus() === 'average',
                        'bg-red-100': getPerformanceStatus() === 'poor'
                     }">
                    <i [class]="'pi ' + getPerformanceIcon()" [ngClass]="getPerformanceColor()"></i>
                </div>
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-sm font-medium text-gray-600">Points de données</p>
                    <p class="text-2xl font-bold text-purple-600">
                        <ng-container *ngIf="!isLoadingRoi(); else loadingSkeleton">
                            {{ roiData()?.platform_roi?.length || 0 }}
                        </ng-container>
                    </p>
                </div>
                <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <i class="pi pi-database text-purple-600"></i>
                </div>
            </div>
        </div>
    </div>

    <div class="bg-white rounded-lg shadow mb-8">
        <div class="p-6 border-b border-gray-200">
            <div class="flex justify-between flex-col lg:flex-row items-center">
                <h2 class="text-xl font-semibold text-gray-800">
                    <i class="pi pi-chart-line mr-2 text-blue-500"></i>
                    Évolution de vos performances
                </h2>

                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-600">Période :</label>
                        <p-dropdown [options]="periodOptions" [ngModel]="selectedPeriod()"
                            (ngModelChange)="onPeriodChange({ value: $event })" optionLabel="label" optionValue="value"
                            placeholder="Choisir une période" styleClass="w-44">
                        </p-dropdown>
                    </div>

                    <button type="button" (click)="refreshRoiData()" [disabled]="isLoadingRoi()"
                        class="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                        pTooltip="Actualiser les données">
                        <i class="pi pi-refresh" [class.animate-spin]="isLoadingRoi()"></i>
                    </button>
                </div>
            </div>
        </div>

        <div class="p-6">
            <div *ngIf="isLoadingRoi()" class="space-y-4">
                <p-skeleton height="300px" borderRadius="8px"></p-skeleton>
                <div class="flex justify-center gap-8">
                    <p-skeleton width="100px" height="20px"></p-skeleton>
                    <p-skeleton width="100px" height="20px"></p-skeleton>
                </div>
            </div>

            <div *ngIf="!isLoadingRoi() && roiChartData" style="height: 400px;">
                <p-chart type="line" [data]="roiChartData" [options]="chartOptions"
                    [style]="{ width: '100%', height: '100%',  display: 'block' }">
                </p-chart>
            </div>

            <div *ngIf="!isLoadingRoi() && !roiData()" class="text-center py-12">
                <i class="pi pi-chart-line text-4xl text-gray-300 mb-4"></i>
                <p class="text-gray-500">Aucune donnée ROI disponible</p>
                <button type="button" (click)="refreshRoiData()"
                    class="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                    Actualiser
                </button>
            </div>
        </div>

        <div *ngIf="!isLoadingRoi() && roiData()" class="px-6 pb-6">
            <div class="bg-gray-50 rounded-lg p-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="flex items-center">
                        <div class="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                        <span class="text-sm text-gray-600">
                            <strong>ROI Coteo :</strong> Performance moyenne de la plateforme
                        </span>
                    </div>
                    <div class="flex items-center">
                        <div class="w-4 h-4 bg-orange-500 rounded mr-3"></div>
                        <span class="text-sm text-gray-600">
                            <strong>Votre ROI :</strong> Vos performances personnelles
                        </span>
                    </div>
                </div>

                <div class="mt-4 pt-4 border-t border-gray-200">
                    <p class="text-xs text-gray-500">
                        <i class="pi pi-info-circle mr-1"></i>
                        Données sur {{ roiData()?.period_days }} jours •
                        Dernière mise à jour : {{ lastUpdate }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</div>

<div class="card">
    <p-table #dt1 [value]="sortedUserHistoryPredictions() || []" dataKey="id" [rows]="10" [loading]="isLoading()"
        [rowHover]="true" [showGridlines]="true" [paginator]="true"
        [globalFilterFields]="['name', 'country.name', 'representative.name', 'status']" responsiveLayout="scroll">
        <ng-template #caption>
            <div class="flex justify-between items-center flex-column sm:flex-row">
                <button pButton label="Supprimer filtres" class="p-button-outlined mb-2" icon="pi pi-filter-slash"
                    (click)="clear(dt1)"></button>

            </div>
        </ng-template>
        <ng-template #header>
            <tr>
                <th style="min-width: 10rem">
                    <div class="flex justify-between items-center">
                        Compétition
                        <p-columnFilter field="name" [showMatchModes]="false" [showAddButton]="false"
                            [showApplyButton]="false" [showOperator]="false" display="menu">
                            <ng-template #filter let-value let-filter="filterCallback">
                                <p-select [ngModel]="value" [options]="competition" (onChange)="filter($event.value)"
                                    placeholder="Any" [style]="{ 'min-width': '12rem' }">
                                    <ng-template let-option #item>
                                        <span [class]="'customer-badge status-' + option.value">{{ option.label
                                            }}</span>
                                    </ng-template>
                                </p-select>
                            </ng-template>
                        </p-columnFilter>
                    </div>
                </th>
                <th style="min-width: 8rem">
                    <div class="flex justify-between items-center">
                        Date
                        <p-columnFilter type="date" [showOperator]="false" [showApplyButton]="false"
                            [showAddButton]="false" [showMatchModes]="false" field="date" display="menu"
                            placeholder="mm/dd/yyyy"></p-columnFilter>
                    </div>
                </th>
                <th style="min-width: 19rem">
                    <div class="flex justify-between items-center">
                        Match

                    </div>
                </th>
                <th style="min-width: 9rem">
                    <div class="flex justify-between items-center">
                        Evénement
                    </div>
                </th>
                <th style="min-width: 12rem">
                    <div class="flex justify-between items-center">
                        Confiance
                        <p-columnFilter field="activity" matchMode="between" [showApplyButton]="false" display="menu"
                            [showMatchModes]="false" [showOperator]="false" [showAddButton]="false">
                            <ng-template #filter let-filter="filterCallback">
                                <p-slider [ngModel]="activityValues" [range]="true" (onSlideEnd)="filter($event.values)"
                                    styleClass="m-3" [style]="{ 'min-width': '12rem' }"></p-slider>
                                <div class="flex items-center justify-between px-2">
                                    <span>{{ activityValues[0] }}</span>
                                    <span>{{ activityValues[1] }}</span>
                                </div>
                            </ng-template>
                        </p-columnFilter>
                    </div>
                </th>
                <th style="min-width: 5rem">
                    <div class="flex justify-between items-center">
                        Cote
                    </div>
                </th>
                <th style="min-width: 8rem">
                    <div class="flex justify-between items-center">
                        Résultat
                    </div>
                </th>
                <th style="min-width: 8rem">
                    <div class="flex justify-between items-center">
                        Abonnement
                    </div>
                </th>
                <th style="min-width: 7rem">
                    <div class="flex justify-between items-center">
                        Suivi
                        <p-columnFilter type="boolean" [showApplyButton]="false" field="verified" display="menu" />
                    </div>
                </th>
            </tr>
        </ng-template>
        <ng-template #body let-prediction>
            <tr>
                <td>
                    {{ prediction.competition_name }}
                </td>
                <td>
                    {{ prediction.event_date | date: 'MM/dd/yyyy' }}
                </td>
                <td>
                    <div class="flex items-center py-3">
                        <div class="flex flex-1 items-center justify-center gap-2">
                            <img [alt]=""
                                [src]="prediction.team_home_logo_id ?  prediction.team_home_logo_id : prediction.team_home_logo"
                                width="37" />
                            <span class="image-text">{{ prediction.team_home_name }}</span>
                        </div>

                        <div class="flex flex-1 items-center justify-center font-extrabold">
                            V.S
                        </div>

                        <div class="flex flex-1 items-center justify-center gap-2">
                            <img [alt]=""
                                [src]="prediction.team_away_logo_id ? prediction.team_away_logo_id : prediction.team_away_logo"
                                width="37" />
                            <span class="image-text">{{ prediction.team_away_name }}</span>
                        </div>
                    </div>
                </td>
                <td>
                    {{ prediction.event_name }}
                </td>
                <td>
                    @if(prediction.confidence_team_home> prediction.confidence_team_away) {
                    <div class="flex flex-col ">

                        <p-progressbar [value]="prediction.confidence_team_home*100" [showValue]="false"
                            [style]="{ height: '0.5rem' }" />
                        <span class="text-center mt-2">
                            {{prediction.confidence_team_home*100}} %
                        </span>
                    </div>
                    } @else {
                    <div class="flex flex-col ">

                        <p-progressbar [value]="prediction.confidence_team_away*100" [showValue]="false"
                            [style]="{ height: '0.5rem' }" />
                        <span class="text-center mt-2">
                            {{prediction.confidence_team_away*100}} %
                        </span>
                    </div>
                    }

                </td>
                <td class="text-center">
                    {{prediction.cote}}
                </td>
                <td class="text-center">



                    <i class="pi" [ngClass]="{
                            'text-green-500 pi-check-circle': prediction.result,
                            'text-red-500 pi-times-circle': !prediction.result
                        }"></i>


                </td>
                <td>
                    {{prediction.subscription_plan_name}}
                </td>
                <td class="text-center">
                    <i class="pi" [ngClass]="{
                            'text-green-500 pi-check-circle': prediction.isfollowed,
                            'text-red-500 pi-times-circle': !prediction.isfollowed
                        }"></i>
                </td>

            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="8">Pas de probabilités</td>
            </tr>
        </ng-template>

    </p-table>
</div>

<ng-template #loadingSkeleton>
    <p-skeleton width="60px" height="32px"></p-skeleton>
</ng-template>